import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CUSTOMER_PORTAL_UPLOAD,
  CUSTOMER_REQUEST_COMMENT_VISIBILITY,
  CUSTOMER_REQUEST_STATUS_TRANSITIONS,
  CUSTOMER_REQUEST_STATUSES,
  getCustomerPortalAllowedExtensions,
} from "@routebot/constants";
import Button from "shared/components/FormElements/Button";
import AreYouSureModal from "components/shared/AreYouSureModal";
import useCustomerPortalApi from "./hooks/useCustomerPortalApi";
import { asId, idsEqual } from "./ids";
import ExtraServiceFacts from "./ExtraServiceFacts";
import "./CustomerPortal.css";

const formatWhen = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return "";
  }
};

const CustomerRequestDetail = ({ variant = "contact", requestId, onChanged, onDeleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = requestId || paramId;
  const isOperator = variant === "operator";
  const isContact = variant === "contact";
  const listPath = isContact ? "/portal/requests" : "/customer-requests";

  const { isLoading, error, clearError, getRequest, addComment, addAttachments, deleteAttachment, deleteRequest, patchStatus, downloadAttachment } =
    useCustomerPortalApi();
  const [doc, setDoc] = useState(null);
  const [message, setMessage] = useState("");
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const threadListRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const accept = useMemo(
    () =>
      getCustomerPortalAllowedExtensions()
        .map((ext) => `.${ext}`)
        .join(","),
    [],
  );
  const closed =
    doc && (doc.status === CUSTOMER_REQUEST_STATUSES.RESOLVED || doc.status === CUSTOMER_REQUEST_STATUSES.CANCELLED);
  const canAttach = doc && !closed;

  const publicComments = useMemo(() => {
    const list = Array.isArray(doc?.comments) ? doc.comments : [];
    return list.filter((item) => item.visibility !== CUSTOMER_REQUEST_COMMENT_VISIBILITY.INTERNAL);
  }, [doc]);

  const teamNotes = useMemo(() => {
    if (!isOperator) return [];
    const list = Array.isArray(doc?.comments) ? doc.comments : [];
    return list.filter((item) => item.visibility === CUSTOMER_REQUEST_COMMENT_VISIBILITY.INTERNAL);
  }, [doc, isOperator]);

  const thread = useMemo(() => {
    if (!doc) return [];
    const opening = doc.body
      ? [
          {
            id: "__opening",
            isOpening: true,
            author: doc.createdBy,
            authorName: doc.createdByName || "",
            createdAt: doc.createdAt,
            body: doc.body,
          },
        ]
      : [];
    return [...opening, ...publicComments];
  }, [doc, publicComments]);

  const setDocAndNotify = (next) => {
    setDoc(next);
    if (next && onChangedRef.current) onChangedRef.current(next);
  };

  const load = useCallback(async () => {
    try {
      const next = await getRequest(id);
      setDoc(next);
    } catch (err) {
      setDoc(null);
    }
  }, [getRequest, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const el = threadListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, id]);

  const nextStatuses = useMemo(() => {
    if (!doc?.status) return [];
    return CUSTOMER_REQUEST_STATUS_TRANSITIONS[doc.status] || [];
  }, [doc?.status]);

  const postComment = async (text, visibility) => {
    const next = await addComment(id, text, visibility);
    setDocAndNotify(next);
  };

  const onSendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    setLocalError("");
    try {
      await postComment(message.trim(), CUSTOMER_REQUEST_COMMENT_VISIBILITY.CUSTOMER);
      setMessage("");
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const onSendNote = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    setLocalError("");
    try {
      await postComment(note.trim(), CUSTOMER_REQUEST_COMMENT_VISIBILITY.INTERNAL);
      setNote("");
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const onStatus = async (status) => {
    setLocalError("");
    try {
      const next = await patchStatus(id, status);
      setDocAndNotify(next);
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const requestStatusConfirm = (status) => {
    if (isOperator && status === CUSTOMER_REQUEST_STATUSES.RESOLVED) {
      setConfirm({ type: "resolve" });
      return;
    }
    if (isOperator && status === CUSTOMER_REQUEST_STATUSES.CANCELLED) {
      setConfirm({ type: "operatorCancel" });
      return;
    }
    onStatus(status);
  };

  const onAddFiles = async (event) => {
    event.preventDefault();
    if (!pendingFiles.length) return;
    setLocalError("");
    try {
      const next = await addAttachments(id, pendingFiles);
      setDocAndNotify(next);
      setPendingFiles([]);
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const onDownload = async (attachmentId, fileName) => {
    if (!attachmentId) return;
    setLocalError("");
    clearError();
    try {
      await downloadAttachment(id, attachmentId, fileName);
    } catch (err) {
      setLocalError(t("customerPortal.downloadFailed"));
    }
  };

  const onDeleteFile = async (attachmentId) => {
    if (!attachmentId) return;
    setLocalError("");
    try {
      const next = await deleteAttachment(id, attachmentId);
      setDocAndNotify(next);
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };

  const onDeleteRequest = async () => {
    setLocalError("");
    try {
      await deleteRequest(id);
      if (onDeleted) onDeleted(id);
      else navigate(listPath, { replace: true });
    } catch (err) {
      setLocalError(err.message || t("customerPortal.saveFailed"));
    }
  };
  const confirmText =
    confirm?.type === "deleteFile"
      ? t("customerPortal.deleteFileConfirm", { name: confirm.fileName })
      : confirm?.type === "deleteRequest"
        ? t("customerPortal.deleteRequestConfirm")
        : confirm?.type === "resolve"
          ? t("customerPortal.resolveConfirm")
          : confirm?.type === "operatorCancel"
            ? t("customerPortal.operatorCancelConfirm")
            : confirm?.type === "cancel"
              ? t("customerPortal.cancelConfirm")
              : t("areYouSure");

  const onConfirm = () => {
    if (!confirm) return;
    if (confirm.type === "deleteFile") {
      onDeleteFile(confirm.attachmentId);
      return;
    }
    if (confirm.type === "deleteRequest") {
      onDeleteRequest();
      return;
    }
    if (confirm.type === "resolve") {
      onStatus(CUSTOMER_REQUEST_STATUSES.RESOLVED);
      return;
    }
    if (confirm.type === "cancel" || confirm.type === "operatorCancel") {
      onStatus(CUSTOMER_REQUEST_STATUSES.CANCELLED);
    }
  };

  if (!doc && isLoading) {
    return (
      <div className="cp-detail-layout">
        <p className="cp-muted cp-inbox-pane-msg">{t("customerPortal.loading")}</p>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="cp-detail-layout">
        <div className="cp-inbox-placeholder">
          <div className="cp-error">{error || t("customerPortal.notFound")}</div>
          <button type="button" className="cp-inbox-back cp-inbox-back--inline" onClick={() => navigate(listPath)}>
            {t("customerPortal.backToList")}
          </button>
        </div>
      </div>
    );
  }

  const filedById = doc.createdBy;
  const isSchoolSide = (item) => item.isOpening || idsEqual(item.author, filedById);
  const isMineBubble = (item) => (isContact ? isSchoolSide(item) : !isSchoolSide(item));

  return (
    <React.Fragment>
    <div className="cp-detail-layout">
      <section className="cp-card cp-thread" aria-label={t("customerPortal.thread")}>
        <div className="cp-thread-head">
          <button type="button" className="cp-inbox-back" onClick={() => navigate(listPath)}>
            {t("customerPortal.backToList")}
          </button>
          <div className="cp-thread-head-main">
            <h2>{doc.title}</h2>
            <p className="cp-muted">
              {t(`customerPortal.types.${doc.type}`, { defaultValue: doc.type })}
              {doc.createdAt ? ` · ${formatWhen(doc.createdAt)}` : ""}
            </p>
          </div>
          <span className={`cp-badge cp-badge--${doc.status}`}>{t(`customerPortal.statuses.${doc.status}`)}</span>
        </div>

        {(localError || error) && <div className="cp-error">{localError || error}</div>}

        <ExtraServiceFacts request={doc} className="cp-trip--thread" />

        <div className="cp-thread-list" ref={threadListRef}>
          {thread.length === 0 && <p className="cp-muted">{t("customerPortal.noThread")}</p>}
          {thread.map((item) => {
            const mine = isMineBubble(item);
            return (
              <article
                key={item.id || item._id || `${item.createdAt}-${item.body}`}
                className={`cp-bubble-row ${mine ? "cp-bubble-row--mine" : "cp-bubble-row--theirs"}${item.isOpening ? " cp-bubble-row--opening" : ""}`}
              >
                <div
                  className={`cp-bubble ${mine ? "cp-bubble--mine" : ""} ${item.isOpening ? "cp-bubble--opening" : ""}`}
                >
                  <div className="cp-comment-meta">
                    {item.isOpening ? t("customerPortal.openingMessage") : item.authorName || ""}
                    {item.createdAt ? ` · ${formatWhen(item.createdAt)}` : ""}
                  </div>
                  <div className="cp-bubble-body">{item.body}</div>
                </div>
              </article>
            );
          })}
        </div>

        <form onSubmit={onSendMessage} className="cp-composer">
          <label className="cp-label" htmlFor="cp-message">
            {t("customerPortal.addMessage")}
          </label>
          <textarea
            id="cp-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={2000}
            placeholder={t("customerPortal.messagePlaceholder")}
          />
          {isOperator && <p className="cp-muted">{t("customerPortal.messageGoesToSchool")}</p>}
          <div className="cp-actions">
            <Button type="submit" disabled={isLoading || !message.trim()}>
              {t("customerPortal.sendMessage")}
            </Button>
          </div>
        </form>
      </section>

      <aside className="cp-sidebar">
        <section className="cp-card">
          <h3 className="cp-card-title">{t("customerPortal.requestDetails")}</h3>
          <dl className="cp-meta cp-meta--stack">
            <div>
              <dt>{t("customerPortal.school")}</dt>
              <dd>{doc.schoolName || "—"}</dd>
            </div>
            {(doc.createdByName || doc.createdByEmail) && (
              <div>
                <dt>{t("customerPortal.filedBy")}</dt>
                <dd>
                  {doc.createdByName || ""}
                  {isOperator && doc.createdByEmail ? ` · ${doc.createdByEmail}` : ""}
                </dd>
              </div>
            )}
            {doc.relatedStudentName && (
              <div>
                <dt>{t("customerPortal.student")}</dt>
                <dd>
                  {doc.relatedStudentName}
                  {doc.relatedStudentIdentifier ? ` · ${doc.relatedStudentIdentifier}` : ""}
                  {doc.relatedStudentLevel ? ` · ${doc.relatedStudentLevel}` : ""}
                </dd>
              </div>
            )}
            {doc.relatedRouteName && (
              <div>
                <dt>{t("customerPortal.service")}</dt>
                <dd>{doc.relatedRouteName}</dd>
              </div>
            )}
            {doc.changeKind && (
              <div>
                <dt>{t("customerPortal.changeKind")}</dt>
                <dd>{t(`customerPortal.changeKinds.${doc.changeKind}`)}</dd>
              </div>
            )}
          </dl>
          <ExtraServiceFacts request={doc} />

          {isOperator && nextStatuses.length > 0 && (
            <div className="cp-sidebar-block">
              <div className="cp-label">{t("customerPortal.updateStatus")}</div>
              <div className="cp-actions cp-actions--tight">
                  {nextStatuses.map((status) => (
                    <Button key={status} type="button" inverse onClick={() => requestStatusConfirm(status)}>
                      {t(`customerPortal.statuses.${status}`)}
                    </Button>
                  ))}
              </div>
            </div>
          )}

          {isContact && doc.status === CUSTOMER_REQUEST_STATUSES.OPEN && (
              <div className="cp-actions cp-actions--tight">
                <Button type="button" danger onClick={() => setConfirm({ type: "cancel" })}>
                  {t("customerPortal.cancelRequest")}
                </Button>
              </div>
          )}
          {isContact && doc.status === CUSTOMER_REQUEST_STATUSES.CANCELLED && (
              <div className="cp-actions cp-actions--tight">
                <Button type="button" danger onClick={() => setConfirm({ type: "deleteRequest" })}>
                  {t("customerPortal.deleteRequest")}
                </Button>
              </div>
          )}
        </section>

        <section className="cp-card">
          <h3 className="cp-card-title">{t("customerPortal.attachments")}</h3>
          {(!Array.isArray(doc.attachments) || doc.attachments.length === 0) && (
            <p className="cp-muted">{t("customerPortal.noFiles")}</p>
          )}
          {(doc.attachments || []).length > 0 && (
            <div className="cp-file-row">
              {(doc.attachments || []).map((file) => {
                const attachmentId = asId(file.id) || asId(file._id);
                return (
                  <div key={attachmentId || file.name} className="cp-file-item">
                    <button
                      type="button"
                      className="cp-file-chip"
                      onClick={() => onDownload(attachmentId, file.name)}
                    >
                      {file.name}
                    </button>
                    {canAttach && (
                      <button
                        type="button"
                        className="cp-file-delete"
                        onClick={() => setConfirm({ type: "deleteFile", attachmentId, fileName: file.name })}
                      >
                        {t("customerPortal.deleteFile")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {canAttach && (
            <form onSubmit={onAddFiles}>
              <label className="cp-label" htmlFor="cp-add-files">
                {t("customerPortal.addFiles")}
              </label>
              <input
                id="cp-add-files"
                type="file"
                multiple
                accept={accept}
                onChange={(event) => {
                  const next = Array.from(event.target.files || []);
                  const tooBig = next.find((file) => file.size > CUSTOMER_PORTAL_UPLOAD.MAX_FILE_BYTES);
                  if (tooBig) {
                    setLocalError(t("customerPortal.fileTooLarge"));
                    return;
                  }
                  if ((doc.attachments || []).length + next.length > CUSTOMER_PORTAL_UPLOAD.MAX_FILES_PER_REQUEST) {
                    setLocalError(t("customerPortal.tooManyFiles"));
                    return;
                  }
                  setLocalError("");
                  setPendingFiles(next);
                }}
              />
              <div className="cp-actions cp-actions--tight">
                <Button type="submit" disabled={isLoading || pendingFiles.length === 0}>
                  {t("customerPortal.uploadFiles")}
                </Button>
              </div>
            </form>
          )}
        </section>

        {isOperator && (
          <section className="cp-card cp-notes" aria-label={t("customerPortal.teamNotes")}>
            <h3 className="cp-card-title">{t("customerPortal.teamNotes")}</h3>
            <p className="cp-muted">{t("customerPortal.teamNotesHint")}</p>
            {teamNotes.length === 0 && <p className="cp-muted">{t("customerPortal.noTeamNotes")}</p>}
            {teamNotes.map((item) => (
              <div key={item.id || item._id || `${item.createdAt}-${item.body}`} className="cp-note">
                <div className="cp-comment-meta">
                  {item.authorName || ""}
                  {item.createdAt ? ` · ${formatWhen(item.createdAt)}` : ""}
                </div>
                <div className="cp-bubble-body">{item.body}</div>
              </div>
            ))}
            <form onSubmit={onSendNote} className="cp-composer">
              <label className="cp-label" htmlFor="cp-note">
                {t("customerPortal.addTeamNote")}
              </label>
              <textarea
                id="cp-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={2000}
                placeholder={t("customerPortal.teamNotePlaceholder")}
              />
              <div className="cp-actions cp-actions--tight">
                <Button type="submit" disabled={isLoading || !note.trim()}>
                  {t("customerPortal.sendTeamNote")}
                </Button>
              </div>
            </form>
          </section>
        )}
      </aside>
    </div>
    <AreYouSureModal
      showModal={Boolean(confirm)}
      text={confirmText}
      onClose={() => setConfirm(null)}
      onConfirmed={onConfirm}
    />
    </React.Fragment>
  );
};

export default CustomerRequestDetail;
